import { daysPerMonth, formatIsoDate, parseIsoDate, getMonthFactor } from 'helpers/DateHelper';
import rentalTypes from 'enums/rentalTypes';
import { add, sub } from 'date-fns';
import purchaseItemTypes from 'enums/purchaseItemTypes';
import vatModes from 'enums/vatModes';
import organizationTypes from 'enums/organizationTypes';
import bookingItemTimePeriodPriceReferenceTypes from 'enums/bookingItemTimePeriodPriceReferenceTypes';
import { roundAmount, calculateVat } from 'helpers/MonetaryHelper';
import { calculateSum, distinct, flattenArray } from 'helpers/ArrayHelper';
import { createPerMonthPurchaseItemsAtDate, createOneTimePurchaseItems, createPeriodBookingPurchaseItems, createRoundingCorrectionPurchaseItem, createSubscriptionBookingRecurringPurchaseItems } from 'helpers/PurchaseItemHelper';
import { getActiveDiscount, getStorageSpecification } from 'helpers/StorageSiteHelper';
import displayPriceModes from 'enums/displayPriceModes';
import bookingItemTypes from 'enums/bookingItemTypes';
import { getTimePeriodPriceOverlap, timePeriodPriceIsActive } from 'logic/timePeriodPriceLogic';
import paymentRecurrences from 'enums/paymentRecurrences';
import strings from 'localization/strings';
import { formatAmount, formatPercentage } from 'helpers/StringHelper';
import { minimumDateString, maximumDateString } from 'logic/bookingLogic';

// frontend version of BookingLogic.CreateBookingPriceInfo
// used when calculating prices dynamically in the frontend
// booking: { subscriptionBooking, currency, startDate, endDate }, startDate and endDate not mandatory
// returns an { perMonth, oneTime, wholePeriod, firstMonth, hasAdditionalServices, hasAdditionalServicesWithUnknownPrice, currency } object
export const createBookingPriceInfo = (booking, bookingItems, appContext) => {
    let datesWithChanges = bookingItems
        .filter(o => o.paymentRecurrence === paymentRecurrences.perMonth.key)
        .map(o => o.timePeriodPrices.map(tpp => formatIsoDate(tpp.startDate ?? booking.startDate, appContext)))
        .concat(bookingItems
            .filter(o => o.paymentRecurrence === paymentRecurrences.perMonth.key)
            .map(o => o.timePeriodPrices.filter(tpp => tpp.endDate).map(tpp => formatIsoDate(add(parseIsoDate(tpp.endDate), { days: 1 }), appContext)))
        );
    datesWithChanges = distinct(flattenArray(datesWithChanges));
    datesWithChanges.sort();

    const hasAdditionalServices = bookingItems.some(o => o.type === bookingItemTypes.service.key);
    const hasAdditionalServicesWithUnknownPrice = bookingItems.some(o => o.type === bookingItemTypes.requestedService.key);
    const oneTimePurchaseItems = createOneTimePurchaseItems(bookingItems, booking.startDate, appContext);
    const periodBookingPurchaseItems = booking.subscriptionBooking
        ? undefined
        : createPeriodBookingPurchaseItems(bookingItems, { startDate: booking.startDate, endDate: booking.endDate }, appContext);
    const firstMonthStartDate = parseIsoDate(booking.startDate) ?? new Date();
    const firstMonthEndDate = add(firstMonthStartDate, { days: daysPerMonth - 1 });
    const firstMonthPurchaseItems = createPeriodBookingPurchaseItems(bookingItems, { startDate: firstMonthStartDate, endDate: firstMonthEndDate }, appContext);

    const bookingPriceInfo = {
        perMonth: datesWithChanges.map(o => createPerMonthPriceInfo(bookingItems, o, appContext)),
        oneTime: createPurchaseItemsPriceInfo(oneTimePurchaseItems, appContext),
        wholePeriod: createPurchaseItemsPriceInfo(periodBookingPurchaseItems, appContext),
        firstMonth: createPurchaseItemsPriceInfo(firstMonthPurchaseItems, appContext),
        hasAdditionalServices: hasAdditionalServices || hasAdditionalServicesWithUnknownPrice,
        hasAdditionalServicesWithUnknownPrice,
        currency: booking.currency
    };

    for (let i = 1; i < bookingPriceInfo.perMonth.length; i++) {
        bookingPriceInfo.perMonth[i].sameAmountAsPrevious =
            bookingPriceInfo.perMonth[i].amounts.totalAmount + bookingPriceInfo.perMonth[i].amounts.vat + bookingPriceInfo.perMonth[i].amounts.roundingCorrection
            ===
            bookingPriceInfo.perMonth[i - 1].amounts.totalAmount + bookingPriceInfo.perMonth[i - 1].amounts.vat + bookingPriceInfo.perMonth[i - 1].amounts.roundingCorrection;
    }

    bookingPriceInfo.hasVat = bookingPriceInfo.oneTime.purchaseItems
        .concat(flattenArray(bookingPriceInfo.perMonth.map(o => o.purchaseItems)))
        .concat(bookingPriceInfo.wholePeriod?.purchaseItems ?? [])
        .some(o => o.vat !== 0);

    return bookingPriceInfo;
};

// used when calculating prices when there is a need to include discount information
// booking: { startDate, endDate, subscriptionBooking, currency, tenantOrganizationType }
// returns an { displayPrice, discountedPrice, nonDiscountedPrice, discount, discountDescription, displayPriceIsDiscounted, untilFurtherDiscount, currency } object
export const createBookingPriceWithDiscountInfo = (booking, storageSite, storageGroup, forcePriceForFirstMonth, priceFormatString, appContext) => {
    const discount = getActiveDiscount(storageSite, storageGroup, booking, true /* validateEligibility */);

    const calculatePrice = applyDiscount => {
        const storageBookingItem = createStorageBookingItem(booking, storageSite, storageGroup, undefined, appContext);
        const bookingItems = [storageBookingItem];
        if(applyDiscount) {
            const discountBookingItem = createDiscountBookingItem(storageSite, storageGroup, storageBookingItem, booking, appContext);
            if(discountBookingItem) {
                bookingItems.push(discountBookingItem);
            }
        }
        const bookingPriceInfo = createBookingPriceInfo(booking, bookingItems, appContext);
        const amounts = booking.subscriptionBooking || forcePriceForFirstMonth
            ? bookingPriceInfo.firstMonth.amounts
            : bookingPriceInfo.wholePeriod.amounts;
        return amounts.totalAmount + amounts.totalVat + amounts.roundingCorrection;
    };

    const info = {
        discountedPrice: calculatePrice(true),
        nonDiscountedPrice: calculatePrice(false),
        currency: storageGroup.currency,
        discount
    };
    info.displayPrice = info.nonDiscountedPrice;

    if(discount) {
        if(discount.numberOfMonths === undefined && discount.percentage > 0) {
            info.discountDescription = strings.formatString(strings.automaticAttentionTexts.xPercentDiscount, formatPercentage(discount.percentage, appContext));
            info.displayPrice = info.discountedPrice; // the discount continues forever; show the discounted price
            info.untilFurtherDiscount = true;
        }
        // discounts that are given only a certain number of months can not be applied to period bookings
        if(booking.subscriptionBooking && discount.numberOfMonths) {
            if(discount.numberOfMonths === 1 && discount.percentage === 1) {
                info.discountDescription = strings.automaticAttentionTexts.firstMonthFree;
            } else if(discount.numberOfMonths === 2 && discount.percentage === 1) {
                info.discountDescription = strings.automaticAttentionTexts.firstTwoMonthsFree;
            } else if(discount.percentage > 0 && discount.percentage < 1) {
                const formattedAmount = strings.formatString(priceFormatString ?? '{0}', formatAmount(info.nonDiscountedPrice, storageGroup.currency, appContext));
                if(discount.numberOfMonths === 1) {
                    info.discountDescription = strings.formatString(strings.automaticAttentionTexts.priceAfterDiscountEndOneMonth, formattedAmount);
                } else {
                    info.discountDescription = strings.formatString(strings.automaticAttentionTexts.priceAfterDiscountEndMultipleMonths, formattedAmount, discount.numberOfMonths);
                }
                info.displayPrice = info.discountedPrice; // show the discounted price
            }
        }
    }

    info.displayPriceIsDiscounted = info.displayPrice !== info.nonDiscountedPrice;

    return info;
};

// returns an { amountExcludingVat, amountIncludingVat, vat, vatRates, currency, displayAmount, roundingCorrection } object for a specific storage group, additional service or booking item
export const getPriceInfo = (object /* storage group, additional service or booking item */, appContext, factor, date, tenantOrganizationType) => {
    const priceInfos = object.timePeriodPrices
        .filter(tpp => date ? timePeriodPriceIsActive(tpp, date, appContext) : tpp.isActive)
        .map(tpp => getPriceInfoHelper((factor ?? 1) * tpp.amount, appContext, object.vatMode /* only set for storage groups */ ?? vatModes.eligibleForVat.key, tpp.vatRate, tenantOrganizationType));

    return mergeToSinglePriceInfo(priceInfos, appContext);
};

export const createEndSubscriptionBookingPriceInfo = (booking, appContext) => {
    const timePeriod = {
        startDate: add(parseIsoDate(booking.subscriptionInfo.invoicedUntilDate), { days: 1 }),
        endDate: parseIsoDate(booking.subscriptionInfo.earliestPossibleEndDate)
    };
    const purchaseItems = createSubscriptionBookingRecurringPurchaseItems(booking.bookingItems, timePeriod, getMonthFactor(timePeriod), appContext);
    return createPurchaseItemsPriceInfo(purchaseItems, appContext);
};

export const createExtendPeriodBookingPriceInfo = (booking, newTimeInterval, appContext) => {
    const bookingStartDate = parseIsoDate(booking.startDate);
    const bookingEndDate = parseIsoDate(booking.endDate);
    const newTimeIntervalStartDate = parseIsoDate(newTimeInterval.startDate);
    const newTimeIntervalEndDate = parseIsoDate(newTimeInterval.endDate);
    const purchaseItems = (newTimeIntervalStartDate < bookingStartDate
        ? createSubscriptionBookingRecurringPurchaseItems(booking.bookingItems, { startDate: newTimeIntervalStartDate, endDate: sub(bookingStartDate, { days: 1 }) }, undefined, appContext)
        : []
    ).concat(newTimeIntervalEndDate > bookingEndDate
        ? createSubscriptionBookingRecurringPurchaseItems(booking.bookingItems, { startDate: add(bookingEndDate, { days: 1 }), endDate: newTimeIntervalEndDate }, undefined, appContext)
        : []
    );

    return createPurchaseItemsPriceInfo(purchaseItems, appContext);
};

// booking: { startDate, endDate, area, tenantOrganizationType }, area only needed when storageGroup.rentalType === rentalTypes.customArea.key
export const createStorageBookingItem = (booking, storageSite, storageGroup, storage, appContext) => ({
    type: bookingItemTypes.storage.key,
    paymentRecurrence: paymentRecurrences.perMonth.key,
    description: getStorageSpecification(booking, storageGroup, storage, appContext),
    timePeriodPrices: createTimePeriodPricesBasedOnStorageGroup(booking, storageSite, storageGroup, appContext),
    currency: storageGroup.currency
});

// booking: { startDate, endDate, area, tenantOrganizationType }, area only needed when storageGroup.rentalType === rentalTypes.customArea.key
export const createTimePeriodPricesBasedOnStorageGroup = (booking, storageSite, storageGroup, appContext) => {
    const timePeriod = {
        startDate: booking.startDate,
        endDate: booking.endDate
    };
    const storageGroupTimePeriodPricesToApply = storageGroup.timePeriodPrices
        .map(tpp => getTimePeriodPriceOverlap(tpp, timePeriod, appContext))
        .filter(o => o.overlaps)
        .map(o => o.timePeriodPrice);

    const amountFactor = storageGroup.rentalType === rentalTypes.customArea.key
        ? booking.area
        : 1;

    const bookingItemTimePeriodPrices = storageGroupTimePeriodPricesToApply
        .map(tpp => {
            const amount = roundAmount(amountFactor * tpp.amount, appContext, false);
            const vatRate = getVatRateForBooking(storageSite, storageGroup, tpp, booking.tenantOrganizationType, appContext);
            return {
                startDate: tpp.startDate,
                endDate: tpp.endDate,
                amount,
                vatRate,
                vat: calculateVat(amount, vatRate, appContext),
                commissionRate: tpp.commissionRate,
                referenceType: bookingItemTimePeriodPriceReferenceTypes.storageGroupTimePeriodPrice.key,
                referenceId: tpp.id
            };
        });

    bookingItemTimePeriodPrices[0].startDate = undefined;
    bookingItemTimePeriodPrices[bookingItemTimePeriodPrices.length - 1].endDate = undefined;

    return bookingItemTimePeriodPrices;
};

export const createDiscountBookingItem = (storageSite, storageGroup, storageBookingItem, booking, appContext) => {
    const activeDiscount = getActiveDiscount(storageSite, storageGroup, booking, true /* validateEligibility */);
    if(!activeDiscount || activeDiscount.actorEligibleForDiscount === false) {
        return undefined;
    }
    return {
        ...storageBookingItem,
        type: bookingItemTypes.discount.key,
        description: strings.discount,
        timePeriodPrices: createDiscountTimePeriodPrices(storageBookingItem.timePeriodPrices, activeDiscount, booking.startDate, appContext)
    };
};

const createDiscountTimePeriodPrices = (timePeriodPrices, discount, startDate, appContext) => {
    let discountTimePeriodPrices = timePeriodPrices
        .map(tpp => {
            const amount = roundAmount(-discount.percentage * tpp.amount, appContext);
            return {
                ...tpp,
                amount,
                vat: tpp.vat === undefined
                    ? undefined
                    : calculateVat(amount, tpp.vatRate, appContext),
            };
        });

    if(discount.numberOfMonths > 0) {
        const startDateAsString = formatIsoDate(startDate, appContext);
        const discountEndDateAsString = formatIsoDate(sub(add(startDate, { months: discount.numberOfMonths }), { days: 1 }), appContext);
        discountTimePeriodPrices = discountTimePeriodPrices
            .map(tpp => {
                const tppStartDate = tpp.startDate ?? minimumDateString;
                const tppEndDate = tpp.endDate ?? maximumDateString;
                if(startDateAsString > tppEndDate || discountEndDateAsString < tppStartDate) {
                    return undefined;
                }
                return {
                    ...tpp,
                    startDate: tppStartDate > startDateAsString ? tppStartDate : startDateAsString,
                    endDate: tppEndDate < discountEndDateAsString ? tppEndDate : discountEndDateAsString
                };
            })
            .filter(o => o);
    }

    return discountTimePeriodPrices;
};

const mergeToSinglePriceInfo = (priceInfos, appContext) => {
    const priceInfo = {
        amountExcludingVat: calculateSum(priceInfos, o => o.amountExcludingVat ?? 0),
        amountIncludingVat: calculateSum(priceInfos, o => o.amountIncludingVat ?? 0),
        vat: calculateSum(priceInfos, o => o.vat ?? 0),
        vatRates: distinct(priceInfos.map(o => o.vatRate)),
        currency: appContext.currency.code
    };

    const roundedAmountExcludingVat = roundAmount(priceInfo.amountExcludingVat, appContext, true);
    const roundedAmountIncludingVat = roundAmount(priceInfo.amountIncludingVat, appContext, true);

    priceInfo.displayAmount = appContext.displayPriceMode === displayPriceModes.excludingVat.key
        ? roundedAmountExcludingVat
        : roundedAmountIncludingVat;
    priceInfo.roundingCorrection = roundedAmountIncludingVat - priceInfo.amountIncludingVat;

    return priceInfo;
};

const getPriceInfoHelper = (amountExcludingVat, appContext, vatMode, vatRate, tenantOrganizationType) => {
    if(amountExcludingVat === undefined) {
        return {};
    }

    const roundedAmountExcludingVat = roundAmount(amountExcludingVat, appContext);
    const adjustedVatRate = getVatRateForVatMode(vatMode, vatRate, tenantOrganizationType);
    const vat = calculateVat(roundedAmountExcludingVat, adjustedVatRate, appContext);

    return {
        amountExcludingVat: roundedAmountExcludingVat,
        amountIncludingVat: roundedAmountExcludingVat + vat,
        vat,
        vatRate: adjustedVatRate ?? 0
    };
};

const createPurchaseItemsPriceInfo = (purchaseItems, appContext) => {
    if (!purchaseItems) {
        return undefined;
    }

    const priceInfo = {
        amounts: createInvoiceAmountInfo(purchaseItems, appContext),
        purchaseItems
    };

    const roundingCorrectionPurchaseItem = createRoundingCorrectionPurchaseItem(priceInfo.amounts.roundingCorrection, appContext);
    if (roundingCorrectionPurchaseItem) {
        priceInfo.purchaseItems.push(roundingCorrectionPurchaseItem);
    }

    return priceInfo;
};

const createPerMonthPriceInfo = (bookingItems, date, appContext) => {
    const perMonthPurchaseItems = createPerMonthPurchaseItemsAtDate(bookingItems, date, appContext);
    const priceInfo = createPurchaseItemsPriceInfo(perMonthPurchaseItems, appContext);
    return {
        date,
        amounts: priceInfo.amounts,
        purchaseItems: priceInfo.purchaseItems
    };
};

const createInvoiceAmountInfo = (purchaseItems, appContext) => {
    const info = {
        storageAmount: getFilteredSum(purchaseItems, o => o.amount, o => o.type === purchaseItemTypes.storage.key),
        storageVat: getFilteredSum(purchaseItems, o => o.vat, o => o.type === purchaseItemTypes.storage.key),
        additionalServicesAmount: getFilteredSum(purchaseItems, o => o.amount, o => o.type !== purchaseItemTypes.storage.key),
        additionalServicesVat: getFilteredSum(purchaseItems, o => o.vat, o => o.type !== purchaseItemTypes.storage.key),
        totalAmount: getFilteredSum(purchaseItems, o => o.amount),
        totalVat: getFilteredSum(purchaseItems, o => o.vat)
    };

    const sum = info.totalAmount + info.totalVat;
    const roundedAmount = roundAmount(sum, appContext, true /* useDisplayPriceRounding */);
    info.roundingCorrection = roundedAmount - sum;

    return info;
};

const getFilteredSum = (purchaseItems, valueSelector, filter) => calculateSum(purchaseItems.filter(o => !filter || filter(o)), valueSelector);

const getVatRateForVatMode = (vatMode, vatRate, tenantOrganizationType) => {
    switch(vatMode) {
        case vatModes.notEligibleForVat.key:
            return 0;

        case vatModes.eligibleForVat.key:
            return vatRate ?? 0;

        case vatModes.businessesOnlyEligibleForVat.key:
            return tenantOrganizationType === organizationTypes.business.key
                ? vatRate ?? 0
                : 0;

        default:
            throw new Error(`Unknown VAT mode: '${JSON.stringify(vatMode)}'`);
    }
};

const getVatRateForBooking = (storageSite, storageGroup, storageGroupTimePeriodPrice, tenantOrganizationType, appContext) => {
    if (storageSite.considerBookingsAsServices) {
        return appContext.defaultVatRate;
    }

    switch (storageGroup.vatMode) {
        case vatModes.eligibleForVat.key:
            return storageGroupTimePeriodPrice.vatRate;

        case vatModes.businessesOnlyEligibleForVat.key:
            return tenantOrganizationType === organizationTypes.business.key
                ? storageGroupTimePeriodPrice.vatRate
                : 0;

        default:
            return 0;
    }
};
